import React, { useState } from 'react';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import _ from 'lodash';

import { useCommunityMembersQuery } from 'generated/graphql';
import { UserSelected } from 'components/UserSelected/UserSelected';

import { CommunityMember } from '../types';

interface MembersListProps {
  communityId: string;
  members: CommunityMember[];
  isExcludingMembers: boolean;
  totalMembers: number;
  removeMember: (member: CommunityMember) => void;
}

const take = 10;
export const MembersList = ({
  communityId,
  members,
  isExcludingMembers,
  totalMembers,
  removeMember,
}: MembersListProps) => {
  const membersQuery = useCommunityMembersQuery({
    // Needed for fetchMore to work without making redundant calls https://github.com/apollographql/apollo-client/issues/7689
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      communityId,
      take,
      excludeUserIds: members.map((m) => m.User.id),
    },
    skip: !isExcludingMembers || !communityId || !take,
  });
  const [moreFetched, setMoreFetched] = useState<CommunityMember[]>([]);
  const membersToRemove: CommunityMember[] | undefined = isExcludingMembers
    ? membersQuery?.data?.community?.members
    : members;
  const total = isExcludingMembers ? totalMembers - members.length : members.length;
  if (!membersToRemove) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  const membersList: CommunityMember[] = _.uniqBy(
    [...membersToRemove, ...moreFetched].filter((member) => {
      const isInList = members.some((m) => m.User.id === member.User.id);
      return isExcludingMembers ? !isInList : isInList;
    }),
    'User.id'
  );
  return (
    <Grid container spacing={1}>
      {membersList.map((member: CommunityMember) => (
        <Grid key={member.User.id} item xs={12} md={6}>
          <UserSelected user={member.User} removeMember={() => removeMember(member)} />
        </Grid>
      ))}
      {isExcludingMembers && membersList.length < total && (
        <Button
          sx={{ mt: 1 }}
          startIcon={membersQuery.loading && <CircularProgress size="1rem" />}
          disabled={membersQuery.loading}
          onClick={async () => {
            const { data } = await membersQuery.fetchMore({
              variables: { skip: membersList.length },
            });
            const extraMembers = data?.community?.members;
            if (extraMembers) {
              setMoreFetched((s) => [...s, ...extraMembers]);
            }
          }}
        >
          Fetch more ({membersList.length}/{total})
        </Button>
      )}
    </Grid>
  );
};
